import { useState, useEffect } from 'react';
import { previewCampaign, createCampaign, fetchTemplates, fetchVariables, sendTestEmail } from '../../api/relances.api';
import ContentEditor from './ContentEditor';

const RELANCE_TYPES = [
  { value: 'PAYMENT_REMINDER', label: 'Rappel de paiement', icon: '💰', description: 'Relancer les paiements en retard' },
  { value: 'OVERDUE_INVOICE', label: 'Facture impayée', icon: '📄', description: 'Relancer les factures non réglées' },
  { value: 'PRESTATION_REMINDER', label: 'Rappel prestation', icon: '🔧', description: 'Relancer les prestations impayées' },
  { value: 'ORDER_FOLLOWUP', label: 'Suivi commande', icon: '📦', description: 'Relancer les commandes en crédit' },
  { value: 'CUSTOM', label: 'Personnalisé', icon: '✏️', description: 'Campagne personnalisée' }
];

const CHANNEL_MODES = [
  { value: 'AUTO_FALLBACK', label: 'Email + WhatsApp', description: 'Email par défaut, WhatsApp si pas d\'email' },
  { value: 'EMAIL_ONLY', label: 'Email uniquement', description: 'Envoyer uniquement par email' },
  { value: 'WHATSAPP_ONLY', label: 'WhatsApp uniquement', description: 'Envoyer uniquement par WhatsApp' }
];

const CONTENT_MODES = [
  { value: 'TEMPLATE', label: 'Template', description: 'Utiliser un template prédéfini' },
  { value: 'TEMPLATE_EDITED', label: 'Template modifié', description: 'Partir d\'un template et personnaliser' },
  { value: 'CUSTOM', label: 'Personnalisé', description: 'Rédiger un message personnalisé' }
];

const DEFAULT_CRITERIA = {
  inactive: { enabled: false, daysInactive: 30 },
  topCustomers: { enabled: false, topN: 50, from: '', to: '' },
  overdue: { enabled: false, minDaysOverdue: 7, maxDaysOverdue: 90 },
  payments: { enabled: false, minDaysOverdue: 7, maxDaysOverdue: 90 },
  prestations: { enabled: false, minDaysOverdue: 7, maxDaysOverdue: 90 },
  orders: { enabled: false, minDaysOverdue: 14 },
  customers: { excludeOptOut: true, cooldownDays: 7, tierLevels: [] }
};

function renderTemplate(template, variables) {
  if (!template) return '';
  return template.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    return variables[key] !== undefined ? variables[key] : match;
  });
}

export default function RelanceBuilder({ onCampaignCreated, onCancel, initialScheduledAt }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [fullRecipients, setFullRecipients] = useState([]);
  const [selectedRecipientIds, setSelectedRecipientIds] = useState(new Set());
  const [previewRecipientId, setPreviewRecipientId] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [testFeedback, setTestFeedback] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [variables, setVariables] = useState({});

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    relanceType: 'PAYMENT_REMINDER',
    channelMode: 'AUTO_FALLBACK',
    contentMode: 'TEMPLATE',
    templateId: '',
    customEmailSubject: '',
    customEmailBody: '',
    customWhatsappMessage: '',
    isDryRun: false,
    scheduledAt: '',
    sequence_delay_days: null,
    sequence_filter: 'ALL',
    targetingCriteria: { ...DEFAULT_CRITERIA }
  });

  useEffect(() => {
    loadTemplates();
    loadVariables();
  }, []);

  useEffect(() => {
    if (initialScheduledAt) {
      setFormData((f) => ({ ...f, scheduledAt: initialScheduledAt }));
    }
  }, [initialScheduledAt]);

  useEffect(() => {
    // Auto-enable targeting based on relance type
    const criteria = { ...DEFAULT_CRITERIA };
    switch (formData.relanceType) {
      case 'PAYMENT_REMINDER':
      case 'OVERDUE_INVOICE':
        criteria.payments.enabled = true;
        break;
      case 'PRESTATION_REMINDER':
        criteria.prestations.enabled = true;
        break;
      case 'ORDER_FOLLOWUP':
        criteria.orders.enabled = true;
        break;
    }
    setFormData(f => ({ ...f, targetingCriteria: criteria }));
  }, [formData.relanceType]);

  const loadTemplates = async () => {
    try {
      const data = await fetchTemplates({ isActive: true });
      setTemplates(data);
    } catch (err) {
      console.error('Error loading templates:', err);
    }
  };

  const loadVariables = async () => {
    try {
      const data = await fetchVariables();
      setVariables(data);
    } catch (err) {
      console.error('Error loading variables:', err);
    }
  };

  const hasCriteria = () => {
    const c = formData.targetingCriteria;
    return (
      c.inactive?.enabled ||
      c.topCustomers?.enabled ||
      c.overdue?.enabled ||
      c.payments?.enabled ||
      c.prestations?.enabled ||
      c.orders?.enabled
    );
  };

  const handlePreview = async () => {
    if (!hasCriteria()) {
      alert('Activez au moins un critère de ciblage avant de prévisualiser.');
      return;
    }
    setLoading(true);
    try {
      const normalizedCriteria = {
        ...formData.targetingCriteria,
        topCustomers: formData.targetingCriteria.topCustomers
          ? {
            ...formData.targetingCriteria.topCustomers,
            from: formData.targetingCriteria.topCustomers.from || undefined,
            to: formData.targetingCriteria.topCustomers.to || undefined
          }
          : undefined
      };
      const data = await previewCampaign({
        relanceType: formData.relanceType,
        targetingCriteria: normalizedCriteria,
        channelMode: formData.channelMode,
        limit: 100
      });
      setPreviewData(data);
      setFullRecipients([]);
      setSelectedRecipientIds(new Set());
      setPreviewRecipientId('');
      setStep(3);
    } catch (err) {
      alert(err.message || 'Erreur lors de la prévisualisation');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadFullList = async () => {
    setLoading(true);
    try {
      const normalizedCriteria = {
        ...formData.targetingCriteria,
        topCustomers: formData.targetingCriteria.topCustomers
          ? {
            ...formData.targetingCriteria.topCustomers,
            from: formData.targetingCriteria.topCustomers.from || undefined,
            to: formData.targetingCriteria.topCustomers.to || undefined
          }
          : undefined
      };
      const data = await previewCampaign({
        relanceType: formData.relanceType,
        targetingCriteria: normalizedCriteria,
        channelMode: formData.channelMode,
        limit: 10000,
        includeRecipients: true
      });
      setPreviewData(data);
      const recipients = data.recipients || [];
      setFullRecipients(recipients);
      setSelectedRecipientIds(new Set(recipients.map(r => r.customerId)));
      if (recipients[0]?.customerId) {
        setPreviewRecipientId(String(recipients[0].customerId));
      }
    } catch (err) {
      alert(err.message || 'Erreur lors du chargement de la liste complète');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      alert('Veuillez donner un nom à la campagne');
      return;
    }

    // Validate template selection for TEMPLATE mode
    if (formData.contentMode === 'TEMPLATE' && !formData.templateId) {
      alert('Veuillez sélectionner un template');
      return;
    }

    // Validate custom content for CUSTOM mode
    if (formData.contentMode === 'CUSTOM') {
      const hasEmail = formData.customEmailSubject && formData.customEmailBody;
      const hasWhatsapp = formData.customWhatsappMessage;
      if (formData.channelMode === 'EMAIL_ONLY' && !hasEmail) {
        alert('Veuillez remplir le sujet et le corps de l\'email');
        return;
      }
      if (formData.channelMode === 'WHATSAPP_ONLY' && !hasWhatsapp) {
        alert('Veuillez remplir le message WhatsApp');
        return;
      }
      if (formData.channelMode === 'AUTO_FALLBACK' && !hasEmail && !hasWhatsapp) {
        alert('Veuillez remplir au moins un contenu (email ou WhatsApp)');
        return;
      }
    }

    setLoading(true);
    try {
      // Build payload - only include templateId if it has a value
      const normalizedCriteria = {
        ...formData.targetingCriteria,
        topCustomers: formData.targetingCriteria.topCustomers
          ? {
            ...formData.targetingCriteria.topCustomers,
            from: formData.targetingCriteria.topCustomers.from || undefined,
            to: formData.targetingCriteria.topCustomers.to || undefined
          }
          : undefined
      };

      const payload = {
        name: formData.name,
        relanceType: formData.relanceType,
        targetingCriteria: normalizedCriteria,
        channelMode: formData.channelMode,
        contentMode: formData.contentMode,
        isDryRun: formData.isDryRun
      };

      // Only include templateId if set and not in CUSTOM mode
      if (formData.contentMode !== 'CUSTOM' && formData.templateId) {
        payload.templateId = formData.templateId;
      }

      // Include custom content if provided
      if (formData.customEmailSubject) payload.customEmailSubject = formData.customEmailSubject;
      if (formData.customEmailBody) payload.customEmailBody = formData.customEmailBody;
      if (formData.customWhatsappMessage) payload.customWhatsappMessage = formData.customWhatsappMessage;
      if (formData.scheduledAt) {
        const iso = new Date(formData.scheduledAt).toISOString();
        payload.scheduledAt = iso;
      }
      if (formData.sequence_delay_days) {
        payload.sequence_delay_days = Number(formData.sequence_delay_days);
        payload.sequence_filter = formData.sequence_filter;
      }

      if (fullRecipients.length > 0 && selectedRecipientIds.size > 0) {
        const selectedIds = Array.from(selectedRecipientIds);
        if (selectedIds.length < fullRecipients.length) {
          payload.targetingCriteria = {
            ...payload.targetingCriteria,
            customers: {
              ...(payload.targetingCriteria.customers || {}),
              includeCustomerIds: selectedIds
            }
          };
        }
      }

      const campaign = await createCampaign(payload);
      onCampaignCreated(campaign);
    } catch (err) {
      alert(err.message || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const handleSendTest = async () => {
    if (!testEmail) {
      setTestFeedback({ type: 'error', message: 'Veuillez saisir un email de test.' });
      return;
    }
    if (!selectedPreviewRecipient) {
      setTestFeedback({ type: 'error', message: 'Aucun destinataire pour le rendu.' });
      return;
    }
    setTestFeedback(null);
    try {
      await sendTestEmail({
        toEmail: testEmail,
        relanceType: formData.relanceType,
        contentMode: formData.contentMode,
        templateId: formData.templateId || undefined,
        customEmailSubject: formData.customEmailSubject || undefined,
        customEmailBody: formData.customEmailBody || undefined,
        templateVariables: previewVariables || {}
      });
      setTestFeedback({ type: 'success', message: 'Email de test envoyé ✅' });
    } catch (err) {
      setTestFeedback({ type: 'error', message: err?.message || 'Erreur envoi test' });
    }
  };

  const updateCriteria = (section, key, value) => {
    setFormData(f => ({
      ...f,
      targetingCriteria: {
        ...f.targetingCriteria,
        [section]: {
          ...f.targetingCriteria[section],
          [key]: value
        }
      }
    }));
  };

  const filteredTemplates = templates.filter(t =>
    t.relance_type === formData.relanceType || formData.relanceType === 'CUSTOM'
  );

  const previewRecipients = fullRecipients.length > 0
    ? fullRecipients
    : (previewData?.sampleRecipients || []);

  const selectedPreviewRecipient = previewRecipients.find(
    r => String(r.customerId) === String(previewRecipientId)
  ) || previewRecipients[0];

  const previewVariables = selectedPreviewRecipient?.templateVariables || {};

  const template = templates.find(t => t.id === formData.templateId);
  const baseEmailSubject = formData.contentMode === 'CUSTOM'
    ? formData.customEmailSubject
    : formData.contentMode === 'TEMPLATE_EDITED'
      ? formData.customEmailSubject || template?.email_subject
      : template?.email_subject;

  const baseEmailBodyHtml = formData.contentMode === 'CUSTOM'
    ? formData.customEmailBody
    : formData.contentMode === 'TEMPLATE_EDITED'
      ? formData.customEmailBody || template?.email_body_html || template?.email_body_text
      : template?.email_body_html || template?.email_body_text;

  const baseEmailBodyText = formData.contentMode === 'CUSTOM'
    ? formData.customEmailBody
    : formData.contentMode === 'TEMPLATE_EDITED'
      ? formData.customEmailBody || template?.email_body_text
      : template?.email_body_text;

  const baseWhatsapp = formData.contentMode === 'CUSTOM'
    ? formData.customWhatsappMessage
    : formData.contentMode === 'TEMPLATE_EDITED'
      ? formData.customWhatsappMessage || template?.whatsapp_message
      : template?.whatsapp_message;

  const renderedSubject = renderTemplate(baseEmailSubject || '', previewVariables);
  const renderedHtml = renderTemplate(baseEmailBodyHtml || '', previewVariables);
  const renderedText = renderTemplate(baseEmailBodyText || '', previewVariables);
  const renderedWhatsapp = renderTemplate(baseWhatsapp || '', previewVariables);

  return (
    <div className="max-w-4xl mx-auto">
      {/* Progress Steps */}
      <div className="flex items-center justify-center mb-8">
        {[1, 2, 3, 4].map(s => (
          <div key={s} className="flex items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= s
                  ? 'bg-slate-900 text-white'
                  : 'bg-slate-200 text-slate-500'
              }`}
            >
              {s}
            </div>
            {s < 4 && (
              <div className={`w-16 h-1 mx-2 ${step > s ? 'bg-slate-900' : 'bg-slate-200'}`} />
            )}
          </div>
        ))}
      </div>
      <div className="flex justify-center gap-8 mb-8 text-sm text-slate-500">
        <span className={step === 1 ? 'text-slate-700 font-medium' : ''}>Type</span>
        <span className={step === 2 ? 'text-slate-700 font-medium' : ''}>Ciblage</span>
        <span className={step === 3 ? 'text-slate-700 font-medium' : ''}>Aperçu</span>
        <span className={step === 4 ? 'text-slate-700 font-medium' : ''}>Contenu</span>
      </div>

      {/* Step 1: Type Selection */}
      {step === 1 && (
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Choisir le type de campagne</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {RELANCE_TYPES.map(type => (
              <button
                key={type.value}
                onClick={() => setFormData(f => ({ ...f, relanceType: type.value }))}
                className={`p-4 border rounded-xl text-left transition-colors ${
                  formData.relanceType === type.value
                    ? 'border-slate-400 bg-slate-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="text-2xl mb-2">{type.icon}</div>
                <div className="font-medium text-slate-900">{type.label}</div>
                <div className="text-sm text-slate-500">{type.description}</div>
              </button>
            ))}
          </div>

          <div className="flex justify-between">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-slate-600 hover:text-slate-800"
            >
              Annuler
            </button>
            <button
              onClick={() => setStep(2)}
              className="px-6 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-950"
            >
              Suivant
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Targeting */}
      {step === 2 && (
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Définir le ciblage</h3>

          {/* Channel Mode */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Canal d'envoi
            </label>
            <div className="grid grid-cols-3 gap-3">
              {CHANNEL_MODES.map(mode => (
                <button
                  key={mode.value}
                  onClick={() => setFormData(f => ({ ...f, channelMode: mode.value }))}
                  className={`p-3 border rounded-xl text-center ${
                    formData.channelMode === mode.value
                      ? 'border-slate-400 bg-slate-50'
                      : 'border-slate-200'
                  }`}
                >
                  <div className="font-medium text-sm text-slate-900">{mode.label}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Targeting Sections */}
          <div className="space-y-4 mb-6">
            {/* Inactive Customers */}
            <div className="border border-slate-200 rounded-xl p-4">
              <label className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  checked={formData.targetingCriteria.inactive?.enabled}
                  onChange={e => updateCriteria('inactive', 'enabled', e.target.checked)}
                  className="rounded"
                />
                <span className="font-medium text-slate-900">😴 Inactifs depuis X jours</span>
              </label>
              {formData.targetingCriteria.inactive?.enabled && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pl-6">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Jours d'inactivité</label>
                    <input
                      type="number"
                      value={formData.targetingCriteria.inactive?.daysInactive || 30}
                      onChange={e => updateCriteria('inactive', 'daysInactive', parseInt(e.target.value) || 1)}
                      className="w-full px-2 py-1 border border-slate-200 rounded text-sm"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Top Customers */}
            <div className="border border-slate-200 rounded-xl p-4">
              <label className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  checked={formData.targetingCriteria.topCustomers?.enabled}
                  onChange={e => updateCriteria('topCustomers', 'enabled', e.target.checked)}
                  className="rounded"
                />
                <span className="font-medium text-slate-900">⭐ Top clients (Top N + période)</span>
              </label>
              {formData.targetingCriteria.topCustomers?.enabled && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pl-6">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Top N</label>
                    <input
                      type="number"
                      value={formData.targetingCriteria.topCustomers?.topN || 50}
                      onChange={e => updateCriteria('topCustomers', 'topN', parseInt(e.target.value) || 1)}
                      className="w-full px-2 py-1 border border-slate-200 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Du</label>
                    <input
                      type="date"
                      value={formData.targetingCriteria.topCustomers?.from || ''}
                      onChange={e => updateCriteria('topCustomers', 'from', e.target.value || '')}
                      className="w-full px-2 py-1 border border-slate-200 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Au</label>
                    <input
                      type="date"
                      value={formData.targetingCriteria.topCustomers?.to || ''}
                      onChange={e => updateCriteria('topCustomers', 'to', e.target.value || '')}
                      className="w-full px-2 py-1 border border-slate-200 rounded text-sm"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Overdue Balances */}
            <div className="border border-slate-200 rounded-xl p-4">
              <label className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  checked={formData.targetingCriteria.overdue?.enabled}
                  onChange={e => updateCriteria('overdue', 'enabled', e.target.checked)}
                  className="rounded"
                />
                <span className="font-medium text-slate-900">⏰ Impayés (balance overdue)</span>
              </label>
              {formData.targetingCriteria.overdue?.enabled && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pl-6">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Min jours retard</label>
                    <input
                      type="number"
                      value={formData.targetingCriteria.overdue?.minDaysOverdue || ''}
                      onChange={e => updateCriteria('overdue', 'minDaysOverdue', parseInt(e.target.value) || 0)}
                      className="w-full px-2 py-1 border border-slate-200 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Max jours retard</label>
                    <input
                      type="number"
                      value={formData.targetingCriteria.overdue?.maxDaysOverdue || ''}
                      onChange={e => updateCriteria('overdue', 'maxDaysOverdue', parseInt(e.target.value) || 0)}
                      className="w-full px-2 py-1 border border-slate-200 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Montant min (€)</label>
                    <input
                      type="number"
                      value={formData.targetingCriteria.overdue?.minAmount || ''}
                      onChange={e => updateCriteria('overdue', 'minAmount', parseFloat(e.target.value) || 0)}
                      className="w-full px-2 py-1 border border-slate-200 rounded text-sm"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Payments */}
            <div className="border border-slate-200 rounded-xl p-4">
              <label className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  checked={formData.targetingCriteria.payments?.enabled}
                  onChange={e => updateCriteria('payments', 'enabled', e.target.checked)}
                  className="rounded"
                />
                <span className="font-medium text-slate-900">💰 Paiements en retard</span>
              </label>
              {formData.targetingCriteria.payments?.enabled && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pl-6">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Min jours retard</label>
                    <input
                      type="number"
                      value={formData.targetingCriteria.payments?.minDaysOverdue || ''}
                      onChange={e => updateCriteria('payments', 'minDaysOverdue', parseInt(e.target.value) || 0)}
                      className="w-full px-2 py-1 border border-slate-200 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Max jours retard</label>
                    <input
                      type="number"
                      value={formData.targetingCriteria.payments?.maxDaysOverdue || ''}
                      onChange={e => updateCriteria('payments', 'maxDaysOverdue', parseInt(e.target.value) || 0)}
                      className="w-full px-2 py-1 border border-slate-200 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Montant min (€)</label>
                    <input
                      type="number"
                      value={formData.targetingCriteria.payments?.minAmount || ''}
                      onChange={e => updateCriteria('payments', 'minAmount', parseFloat(e.target.value) || 0)}
                      className="w-full px-2 py-1 border border-slate-200 rounded text-sm"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Prestations */}
            <div className="border border-slate-200 rounded-xl p-4">
              <label className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  checked={formData.targetingCriteria.prestations?.enabled}
                  onChange={e => updateCriteria('prestations', 'enabled', e.target.checked)}
                  className="rounded"
                />
                <span className="font-medium text-slate-900">🔧 Prestations impayées</span>
              </label>
              {formData.targetingCriteria.prestations?.enabled && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pl-6">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Min jours retard</label>
                    <input
                      type="number"
                      value={formData.targetingCriteria.prestations?.minDaysOverdue || ''}
                      onChange={e => updateCriteria('prestations', 'minDaysOverdue', parseInt(e.target.value) || 0)}
                      className="w-full px-2 py-1 border border-slate-200 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Max jours retard</label>
                    <input
                      type="number"
                      value={formData.targetingCriteria.prestations?.maxDaysOverdue || ''}
                      onChange={e => updateCriteria('prestations', 'maxDaysOverdue', parseInt(e.target.value) || 0)}
                      className="w-full px-2 py-1 border border-slate-200 rounded text-sm"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Orders */}
            <div className="border border-slate-200 rounded-xl p-4">
              <label className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  checked={formData.targetingCriteria.orders?.enabled}
                  onChange={e => updateCriteria('orders', 'enabled', e.target.checked)}
                  className="rounded"
                />
                <span className="font-medium text-slate-900">📦 Commandes en crédit</span>
              </label>
              {formData.targetingCriteria.orders?.enabled && (
                <div className="grid grid-cols-2 gap-4 pl-6">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Min jours depuis commande</label>
                    <input
                      type="number"
                      value={formData.targetingCriteria.orders?.minDaysOverdue || ''}
                      onChange={e => updateCriteria('orders', 'minDaysOverdue', parseInt(e.target.value) || 0)}
                      className="w-full px-2 py-1 border border-slate-200 rounded text-sm"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Customer Filters */}
            <div className="border border-slate-200 rounded-xl p-4">
              <div className="font-medium text-slate-900 mb-3">👤 Filtres clients</div>
              <div className="grid grid-cols-2 gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.targetingCriteria.customers?.excludeOptOut}
                    onChange={e => updateCriteria('customers', 'excludeOptOut', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm text-slate-700">Exclure opt-out</span>
                </label>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Cooldown (jours)</label>
                  <input
                    type="number"
                    value={formData.targetingCriteria.customers?.cooldownDays || 7}
                    onChange={e => updateCriteria('customers', 'cooldownDays', parseInt(e.target.value) || 0)}
                    className="w-full px-2 py-1 border border-slate-200 rounded text-sm"
                  />
                </div>
              </div>

              {/* Badge filter */}
              <div className="mt-4">
                <label className="block text-xs text-slate-500 mb-2">
                  Filtrer par badge
                  <span className="ml-1 text-slate-400">(aucun sélectionné = tous les badges)</span>
                </label>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { value: 'bronze', icon: '🥉', label: 'Bronze', on: 'bg-orange-50 text-orange-700 ring-1 ring-orange-300', off: 'bg-white text-slate-400 ring-1 ring-slate-200 hover:ring-slate-300' },
                    { value: 'silver', icon: '🥈', label: 'Silver', on: 'bg-slate-100 text-slate-700 ring-1 ring-slate-400', off: 'bg-white text-slate-400 ring-1 ring-slate-200 hover:ring-slate-300' },
                    { value: 'gold',   icon: '🥇', label: 'Gold',   on: 'bg-yellow-50 text-yellow-700 ring-1 ring-yellow-300', off: 'bg-white text-slate-400 ring-1 ring-slate-200 hover:ring-slate-300' },
                  ].map(tier => {
                    const tiers = formData.targetingCriteria.customers?.tierLevels || [];
                    const active = tiers.includes(tier.value);
                    return (
                      <button
                        key={tier.value}
                        type="button"
                        onClick={() => {
                          const next = active
                            ? tiers.filter(t => t !== tier.value)
                            : [...tiers, tier.value];
                          updateCriteria('customers', 'tierLevels', next);
                        }}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${active ? tier.on : tier.off}`}
                      >
                        <span>{tier.icon}</span>
                        <span>{tier.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-between">
            <button
              onClick={() => setStep(1)}
              className="px-4 py-2 text-slate-600 hover:text-slate-800"
            >
              Retour
            </button>
            <button
              onClick={handlePreview}
              disabled={loading || !hasCriteria()}
              title={!hasCriteria() ? 'Activez au moins un critère de ciblage' : undefined}
              className="px-6 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-950 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Chargement...' : 'Prévisualiser'}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Preview */}
      {step === 3 && previewData && (
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Aperçu de la campagne</h3>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="text-sm text-slate-500 mb-1">Total</div>
              <div className="text-2xl font-bold text-slate-900">{previewData.stats.total}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="text-sm text-slate-500 mb-1">📧 Email</div>
              <div className="text-2xl font-bold text-slate-700">{previewData.stats.byChannel.EMAIL}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="text-sm text-slate-500 mb-1">📱 WhatsApp</div>
              <div className="text-2xl font-bold text-green-600">{previewData.stats.byChannel.WHATSAPP}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="text-sm text-slate-500 mb-1">⚠ Non joignable</div>
              <div className="text-2xl font-bold text-orange-500">{previewData.stats.byChannel.NONE}</div>
            </div>
          </div>

          {/* Additional Stats */}
          <div className="bg-slate-50 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-slate-500">Montant total:</span>{' '}
                <span className="font-medium">{previewData.stats.totalAmount?.toFixed(2)} €</span>
              </div>
              <div>
                <span className="text-slate-500">Retard moyen:</span>{' '}
                <span className="font-medium">{previewData.stats.avgDaysOverdue} jours</span>
              </div>
              <div>
                <span className="text-slate-500">Taux joignabilité:</span>{' '}
                <span className="font-medium">
                  {previewData.stats.total > 0
                    ? Math.round((previewData.stats.reachable / previewData.stats.total) * 100)
                    : 0}%
                </span>
              </div>
            </div>
          </div>

          {/* Sample Recipients */}
          <div className="mb-6">
            <h4 className="font-medium text-slate-900 mb-3">Exemple de destinataires</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 px-3 text-slate-500">Client</th>
                    <th className="text-left py-2 px-3 text-slate-500">Contact</th>
                    <th className="text-center py-2 px-3 text-slate-500">Canal</th>
                    <th className="text-right py-2 px-3 text-slate-500">Montant</th>
                    <th className="text-right py-2 px-3 text-slate-500">Retard</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.sampleRecipients.map((r, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="py-2 px-3 font-medium text-slate-900">{r.customerName}</td>
                      <td className="py-2 px-3 text-slate-500">{r.customerEmail || r.customerPhone || '-'}</td>
                      <td className="py-2 px-3 text-center">
                        {r.resolvedChannel === 'EMAIL' ? '📧' : r.resolvedChannel === 'WHATSAPP' ? '📱' : '⚠'}
                      </td>
                      <td className="py-2 px-3 text-right">{r.amount?.toFixed(2)} €</td>
                      <td className="py-2 px-3 text-right">{r.daysOverdue}j</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Full Recipients List */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-slate-900">Liste complète avant préparation</h4>
              <button
                type="button"
                onClick={handleLoadFullList}
                disabled={loading}
                className="px-3 py-2 border border-slate-200 rounded-lg bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
              >
                {loading ? 'Chargement...' : 'Charger la liste complète'}
              </button>
            </div>
            {fullRecipients.length > 0 && (
              <div className="text-sm text-slate-600 mb-2">
                {selectedRecipientIds.size} destinataire(s) sélectionné(s)
              </div>
            )}
            {fullRecipients.length === 0 ? (
              <div className="text-sm text-slate-500 border border-slate-200 rounded-lg p-4">
                Charge la liste complète pour sélectionner/désélectionner avant le prepare.
              </div>
            ) : (
              <div className="border border-slate-200 rounded-lg overflow-y-auto max-h-80">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left py-2 px-3 font-medium text-slate-500">Sel.</th>
                      <th className="text-left py-2 px-3 font-medium text-slate-500">Client</th>
                      <th className="text-left py-2 px-3 font-medium text-slate-500">Contact</th>
                      <th className="text-center py-2 px-3 font-medium text-slate-500">Canal</th>
                      <th className="text-right py-2 px-3 font-medium text-slate-500">Montant</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fullRecipients.map((r, i) => {
                      const checked = selectedRecipientIds.has(r.customerId);
                      return (
                        <tr key={`${r.customerId}-${i}`} className="border-b border-slate-100">
                          <td className="py-2 px-3">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                const next = new Set(selectedRecipientIds);
                                if (e.target.checked) next.add(r.customerId);
                                else next.delete(r.customerId);
                                setSelectedRecipientIds(next);
                              }}
                              className="h-4 w-4"
                            />
                          </td>
                          <td className="py-2 px-3 font-medium text-slate-900">{r.customerName}</td>
                          <td className="py-2 px-3 text-slate-500">{r.customerEmail || r.customerPhone || '-'}</td>
                          <td className="py-2 px-3 text-center">
                            {r.resolvedChannel === 'EMAIL' ? '📧' : r.resolvedChannel === 'WHATSAPP' ? '📱' : '⚠'}
                          </td>
                          <td className="py-2 px-3 text-right">{r.amount?.toFixed(2)} €</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Preview Message on Real Recipient */}
          <div className="mb-6">
            <h4 className="font-medium text-slate-900 mb-3">Preview message réel</h4>
            {previewRecipients.length === 0 ? (
              <div className="text-sm text-slate-500 border border-slate-200 rounded-lg p-4">
                Aucun destinataire disponible pour le preview.
              </div>
            ) : (
              <div className="space-y-4">
                <div className="border border-slate-200 rounded-lg p-4 bg-white">
                  <div className="text-sm font-medium text-slate-900 mb-2">Envoyer un test</div>
                  <div className="flex flex-wrap gap-2 items-center">
                    <input
                      type="email"
                      placeholder="Email de test"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      className="px-3 py-2 border border-slate-200 rounded-lg bg-white text-sm w-64"
                    />
                    <button
                      type="button"
                      onClick={handleSendTest}
                      className="px-3 py-2 bg-slate-900 text-white rounded-lg text-sm font-semibold hover:bg-slate-950"
                    >
                      Envoyer un test
                    </button>
                  </div>
                  {testFeedback && (
                    <div className={`mt-2 text-sm ${testFeedback.type === 'success' ? 'text-emerald-600' : 'text-red-600'}`}>
                      {testFeedback.message}
                    </div>
                  )}
                  <div className="text-xs text-slate-500 mt-1">Aucun envoi réel ni queue, uniquement un test SMTP.</div>
                </div>
                <div className="flex flex-wrap gap-3 items-center">
                  <label className="text-sm text-slate-600">Client :</label>
                  <select
                    value={previewRecipientId || String(previewRecipients[0].customerId)}
                    onChange={(e) => setPreviewRecipientId(e.target.value)}
                    className="px-3 py-2 border border-slate-200 rounded-lg bg-white text-sm"
                  >
                    {previewRecipients.map((r, i) => (
                      <option key={`${r.customerId}-${i}`} value={String(r.customerId)}>
                        {r.customerName} ({r.customerEmail || r.customerPhone || '—'})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="border border-slate-200 rounded-lg p-4 bg-white">
                    <div className="text-xs font-medium text-slate-500 mb-1">HTML rendu</div>
                    <div className="border border-slate-100 rounded p-3 bg-slate-50" dangerouslySetInnerHTML={{ __html: renderedHtml || '<em>Aucun HTML</em>' }} />
                  </div>
                  <div className="border border-slate-200 rounded-lg p-4 bg-white">
                    <div className="text-xs font-medium text-slate-500 mb-1">Texte brut</div>
                    <div className="text-sm text-slate-900 whitespace-pre-wrap bg-slate-50 rounded p-3">
                      {renderedText || '-'}
                    </div>
                  </div>
                  <div className="border border-slate-200 rounded-lg p-4 bg-white">
                    <div className="text-xs font-medium text-slate-500 mb-1">Sujet</div>
                    <div className="text-sm text-slate-900 bg-slate-50 rounded p-3">
                      {renderedSubject || '-'}
                    </div>
                  </div>
                  <div className="border border-slate-200 rounded-lg p-4 bg-white">
                    <div className="text-xs font-medium text-slate-500 mb-1">WhatsApp</div>
                    <div className="text-sm text-slate-900 bg-slate-50 rounded p-3">
                      {renderedWhatsapp || '-'}
                    </div>
                  </div>
                </div>

                <div className="border border-slate-200 rounded-lg p-4 bg-white">
                  <div className="text-xs font-medium text-slate-500 mb-1">Variables remplacées</div>
                  <div className="text-xs text-slate-700 bg-slate-50 rounded p-3 font-mono">
                    {JSON.stringify(previewVariables, null, 2)}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-between">
            <button
              onClick={() => setStep(2)}
              className="px-4 py-2 text-slate-600 hover:text-slate-800"
            >
              Modifier le ciblage
            </button>
            <button
              onClick={() => setStep(4)}
              className="px-6 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-950"
            >
              Configurer le contenu
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Content & Create */}
      {step === 4 && (
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Configurer le contenu</h3>

          {/* Campaign Name */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Nom de la campagne *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={e => setFormData(f => ({ ...f, name: e.target.value }))}
              placeholder="Ex: Relance paiements janvier 2025"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-white text-slate-900"
            />
          </div>

          {/* Scheduling */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Programmation (optionnelle)
            </label>
            <div className="flex flex-wrap items-center gap-3">
              <input
                type="datetime-local"
                value={formData.scheduledAt}
                onChange={e => setFormData(f => ({ ...f, scheduledAt: e.target.value }))}
                className="px-3 py-2 border border-slate-200 rounded-lg bg-white text-slate-900"
              />
              <a
                href="/agenda"
                className="px-3 py-2 border border-slate-200 rounded-lg text-slate-700 hover:bg-slate-50 text-sm"
              >
                📅 Planifier dans l’agenda
              </a>
              <span className="text-xs text-slate-500">Le scheduler backend exécutera la campagne à l’heure choisie.</span>
            </div>
          </div>

          {/* Content Mode */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Mode de contenu
            </label>
            <div className="grid grid-cols-3 gap-3">
              {CONTENT_MODES.map(mode => (
                <button
                  key={mode.value}
                  onClick={() => setFormData(f => ({ ...f, contentMode: mode.value }))}
                  className={`p-3 border rounded-xl text-center ${
                    formData.contentMode === mode.value
                      ? 'border-slate-400 bg-slate-50'
                      : 'border-slate-200'
                  }`}
                >
                  <div className="font-medium text-sm text-slate-900">{mode.label}</div>
                  <div className="text-xs text-slate-500">{mode.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Template Selection */}
          {formData.contentMode !== 'CUSTOM' && (
            <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Template
            </label>
            <select
              value={formData.templateId}
              onChange={e => setFormData(f => ({ ...f, templateId: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-white text-slate-900"
            >
              <option value="">Sélectionner un template</option>
              {filteredTemplates.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          )}

          {/* Custom Content / Editor */}
          {(formData.contentMode === 'CUSTOM' || formData.contentMode === 'TEMPLATE_EDITED') && (
            <ContentEditor
              emailSubject={formData.customEmailSubject}
              emailBody={formData.customEmailBody}
              whatsappMessage={formData.customWhatsappMessage}
              channelMode={formData.channelMode}
              variables={variables[formData.relanceType] || []}
              onChange={(field, value) => setFormData(f => ({ ...f, [field]: value }))}
            />
          )}

          {/* Suivi automatique (séquence) */}
          <div className="mb-6 p-4 bg-violet-50 border border-violet-200 rounded-lg">
            <label className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                checked={!!formData.sequence_delay_days}
                onChange={e => setFormData(f => ({ ...f, sequence_delay_days: e.target.checked ? 7 : null }))}
                className="rounded"
              />
              <span className="font-medium text-violet-800">Activer un suivi automatique</span>
            </label>
            {!!formData.sequence_delay_days && (
              <div className="pl-6 space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-violet-700">Relancer après</span>
                  <input
                    type="number"
                    min={1}
                    max={365}
                    value={formData.sequence_delay_days}
                    onChange={e => setFormData(f => ({ ...f, sequence_delay_days: parseInt(e.target.value) || 7 }))}
                    className="w-20 px-2 py-1 border border-violet-300 rounded bg-white text-sm text-center"
                  />
                  <span className="text-sm text-violet-700">jours</span>
                </div>
                <div className="flex gap-4 text-sm">
                  <label className="flex items-center gap-1.5">
                    <input
                      type="radio"
                      checked={formData.sequence_filter === 'ALL'}
                      onChange={() => setFormData(f => ({ ...f, sequence_filter: 'ALL' }))}
                    />
                    <span className="text-violet-800">Tous les destinataires</span>
                  </label>
                  <label className="flex items-center gap-1.5">
                    <input
                      type="radio"
                      checked={formData.sequence_filter === 'UNSENT_ONLY'}
                      onChange={() => setFormData(f => ({ ...f, sequence_filter: 'UNSENT_ONLY' }))}
                    />
                    <span className="text-violet-800">Seulement ceux non envoyés</span>
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Dry Run Option */}
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.isDryRun}
                onChange={e => setFormData(f => ({ ...f, isDryRun: e.target.checked }))}
                className="rounded"
              />
              <span className="font-medium text-amber-800">Mode test (dry run)</span>
            </label>
            <p className="text-sm text-amber-700 mt-1 pl-6">
              Aucun message ne sera réellement envoyé. Utile pour tester le ciblage.
            </p>
          </div>

          <div className="flex justify-between">
            <button
              onClick={() => setStep(3)}
              className="px-4 py-2 text-slate-600 hover:text-slate-800"
            >
              Retour
            </button>
            <button
              onClick={handleCreate}
              disabled={loading || !formData.name.trim()}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {loading ? 'Création...' : 'Créer la campagne'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
